import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  Grid,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  School as SchoolIcon,
  HomeWork as CampIcon,
  AccessTime as ClockIcon,
  People as PeopleIcon,
  MailOutline as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  Event as CalendarIcon,
  SwapHoriz as SwapHorizIcon,
  History as HistoryIcon,
  HourglassEmpty as HourglassIcon,
} from "@mui/icons-material";
import {
  useLimitsData,
  useAssignedTrainer,
  useMyTrainerRequests,
} from "../../../hooks/useTrainers";
import RequestTrainerModal from "./RequestTrainerModal";

function fmtDate(dateStr) {
  if (!dateStr) return "29 Aug 2027";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function AssignTrainerTab() {
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  // Feedback Notification
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // React Query for Limits Data
  const { data: limitsData, isLoading: limitsLoading } = useLimitsData();

  // React Query for Assigned Trainer Data
  const { data: assignedTrainerData, isLoading: trainerLoading } = useAssignedTrainer();

  // React Query for Institute's trainer change requests
  const { data: myRequestsData } = useMyTrainerRequests({ page: 1, limit: 5 });
  const pendingRequest = (myRequestsData?.data || []).find(
    (r) => String(r.status).toLowerCase() === "pending"
  );
  const hasPending = Boolean(pendingRequest);

  // Parse Assigned Trainers List from /trainer_assign/my-trainers
  const trainers = React.useMemo(() => {
    if (assignedTrainerData) {
      let list = [];
      if (Array.isArray(assignedTrainerData)) {
        list = assignedTrainerData;
      } else if (Array.isArray(assignedTrainerData?.assigned_trainers)) {
        list = assignedTrainerData.assigned_trainers;
      } else if (Array.isArray(assignedTrainerData?.trainers)) {
        list = assignedTrainerData.trainers;
      } else if (assignedTrainerData?.trainer) {
        list = [assignedTrainerData.trainer];
      }

      if (list && list.length > 0) {
        return list.map((item, idx) => {
          const fdpList = (item.domains?.fdp || []).map((d) => (typeof d === "string" ? d : d.domain_name));
          const techcampList = (item.domains?.techcamp || []).map((d) => (typeof d === "string" ? d : d.domain_name));
          const allSkills = Array.from(new Set([...fdpList, ...techcampList]));

          return {
            id: item.trainer_id || item.id || idx + 1,
            fullname: item.trainer_name || item.fullname || item.name || "Assigned Trainer",
            skills: allSkills,
            fdpDomains: fdpList,
            techcampDomains: techcampList,
            email: item.email || "",
            phone: item.phone || item.mobile_number || "",
          };
        });
      }
    }

    return [];
  }, [assignedTrainerData]);

  const handleTrainerRequested = (result) => {
    if (result?.success) {
      setSnackbar({
        open: true,
        message: result.message || `Trainer change request for ${result.trainer?.fullname || "trainer"} submitted successfully and sent for review!`,
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message: result?.error || "Failed to submit trainer change request.",
        severity: "error",
      });
    }
  };

  // Parse Limits
  const fdp = limitsData?.fdp ?? {};
  const educator = fdp.educator_limit ?? { total: 30, used: 0, left: 30 };
  const fdpExpiry = fdp.expired_date ?? null;

  const techCamp = limitsData?.tech_camp ?? {};
  const techCampTotal = techCamp.total ?? techCamp.techcamp_limit?.total ?? 3;
  const techCampUsed = techCamp.used ?? techCamp.techcamp_limit?.used ?? 3;
  const techCampLeft =
    techCamp.left ??
    techCamp.techcamp_limit?.left ??
    Math.max(0, techCampTotal - techCampUsed);
  const techCampExpiry = techCamp.expired_date ?? null;

  const expiryDate = fdpExpiry || techCampExpiry || "2027-08-29";

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
      {/* ─────────────────────────────────────────────────────────────────
          CARD 1: FDP & TECH CAMP LIMITS
      ───────────────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: "14px",
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Header Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              color: "#1e293b",
              letterSpacing: "-0.01em",
            }}
          >
            FDP &amp; Tech Camp Limits
          </Typography>

          {/* Expiry Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.22)",
              borderRadius: "20px",
              px: 1.5,
              py: 0.45,
            }}
          >
            <ClockIcon sx={{ fontSize: "14px", color: "#ef4444" }} />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#dc2626",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Expires {fmtDate(expiryDate)}
            </Typography>
          </Box>
        </Box>

        {/* 2-Card Grid */}
        {limitsLoading ? (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={130} sx={{ borderRadius: "12px" }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={130} sx={{ borderRadius: "12px" }} />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2.5}>
            {/* FDP Educator Limit Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #bbf7d0",
                  backgroundColor: "#f9fefb",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 14px rgba(34, 197, 94, 0.12)",
                    borderColor: "#86efac",
                  },
                }}
              >
                {/* Title with Green Square Icon */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      backgroundColor: "#16a34a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      flexShrink: 0,
                      boxShadow: "0 2px 6px rgba(22, 163, 74, 0.25)",
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: "1.2rem" }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.88rem",
                      color: "#334155",
                    }}
                  >
                    FDP Educator Limit
                  </Typography>
                </Box>

                {/* Stat Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    mt: 2.5,
                    mb: 1.5,
                    px: 0.5,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "1.45rem",
                        fontWeight: 700,
                        color: "#0f172a",
                        lineHeight: 1,
                      }}
                    >
                      {educator.total}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#64748b",
                        mt: 0.5,
                      }}
                    >
                      Approved
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "1.45rem",
                        fontWeight: 700,
                        color: "#ea580c",
                        lineHeight: 1,
                      }}
                    >
                      {educator.used}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#64748b",
                        mt: 0.5,
                      }}
                    >
                      Used
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontSize: "1.45rem",
                        fontWeight: 700,
                        color: "#16a34a",
                        lineHeight: 1,
                      }}
                    >
                      {educator.left}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#64748b",
                        mt: 0.5,
                      }}
                    >
                      Available
                    </Typography>
                  </Box>
                </Box>

                {/* Progress Bar */}
                <Box
                  sx={{
                    width: "100%",
                    height: 5,
                    borderRadius: 3,
                    backgroundColor: "rgba(22, 163, 74, 0.15)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${
                        educator.total > 0
                          ? Math.min(100, Math.round((educator.used / educator.total) * 100))
                          : 0
                      }%`,
                      height: "100%",
                      backgroundColor: "#16a34a",
                      borderRadius: 3,
                    }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Tech Camp Limit Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #fecaca",
                  backgroundColor: "#fffafa",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 14px rgba(239, 68, 68, 0.12)",
                    borderColor: "#fca5a5",
                  },
                }}
              >
                {/* Title with Red Square Icon */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      backgroundColor: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      flexShrink: 0,
                      boxShadow: "0 2px 6px rgba(239, 68, 68, 0.25)",
                    }}
                  >
                    <CampIcon sx={{ fontSize: "1.2rem" }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.88rem",
                      color: "#334155",
                    }}
                  >
                    Tech Camp Limit
                  </Typography>
                </Box>

                {/* Stat Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    mt: 2.5,
                    mb: 1.5,
                    px: 0.5,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "1.45rem",
                        fontWeight: 700,
                        color: "#0f172a",
                        lineHeight: 1,
                      }}
                    >
                      {techCampTotal}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#64748b",
                        mt: 0.5,
                      }}
                    >
                      Approved
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "1.45rem",
                        fontWeight: 700,
                        color: "#ea580c",
                        lineHeight: 1,
                      }}
                    >
                      {techCampUsed}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#64748b",
                        mt: 0.5,
                      }}
                    >
                      Used
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontSize: "1.45rem",
                        fontWeight: 700,
                        color: "#dc2626",
                        lineHeight: 1,
                      }}
                    >
                      {techCampLeft}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#64748b",
                        mt: 0.5,
                      }}
                    >
                      Available
                    </Typography>
                  </Box>
                </Box>

                {/* Progress Bar */}
                <Box
                  sx={{
                    width: "100%",
                    height: 5,
                    borderRadius: 3,
                    backgroundColor: "rgba(239, 68, 68, 0.15)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${
                        techCampTotal > 0
                          ? Math.min(100, Math.round((techCampUsed / techCampTotal) * 100))
                          : 0
                      }%`,
                      height: "100%",
                      backgroundColor: "#ef4444",
                      borderRadius: 3,
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* ─────────────────────────────────────────────────────────────────
          CARD 2: TRAINER DETAILS
      ───────────────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: "14px",
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Header Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 3,
          }}
        >
          {/* Left Title & Icon */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                backgroundColor: "#eff6ff",
                border: "1px solid #dbeafe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2563eb",
                flexShrink: 0,
              }}
            >
              <PeopleIcon sx={{ fontSize: "1.25rem" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  color: "#0f172a",
                  letterSpacing: "-0.01em",
                }}
              >
                Trainer Details
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#64748b",
                  fontWeight: 400,
                  mt: 0.2,
                }}
              >
                Here is the trainer associated with your FDP &amp; Tech Camp programs.
              </Typography>
            </Box>
          </Box>

          {/* Right Action: Single Change Trainer / Pending Request Button */}
          {trainers.length > 0 && (
            <Button
              variant="outlined"
              startIcon={
                hasPending ? (
                  <HourglassIcon sx={{ fontSize: "18px !important" }} />
                ) : (
                  <SwapHorizIcon sx={{ fontSize: "18px !important" }} />
                )
              }
              onClick={() => setRequestModalOpen(true)}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                textTransform: "none",
                borderRadius: "8px",
                bgcolor: hasPending ? "#FFFBEB" : "#eff6ff",
                borderColor: hasPending ? "#FDE68A" : "#bfdbfe",
                color: hasPending ? "#D97706" : "#2563eb",
                fontWeight: 600,
                fontSize: "0.82rem",
                px: 1.75,
                py: 0.6,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: hasPending ? "#FEF3C7" : "#dbeafe",
                  borderColor: hasPending ? "#FCD34D" : "#93c5fd",
                  boxShadow: "none",
                },
              }}
            >
              {hasPending ? "Change Request Pending" : "Change Trainer"}
            </Button>
          )}
        </Box>

        {/* Trainers List */}
        {trainerLoading ? (
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: "12px" }} />
        ) : trainers.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "12px",
              border: "1px dashed #cbd5e1",
              backgroundColor: "#f8fafc",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <PeopleIcon sx={{ fontSize: 36, color: "#94a3b8" }} />
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#334155" }}>
              No Trainer Assigned Yet
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#64748b", maxWidth: 400 }}>
              Your institute does not have an assigned trainer yet.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {trainers.map((trainer) => (
              <Paper
                key={trainer.id}
                elevation={0}
                sx={{
                  p: { xs: 2.25, sm: 2.75 },
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#ffffff",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#cbd5e1",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  {/* Left Column: Trainer Name */}
                  <Grid item xs={12} sm={6} md={3.5} lg={3.5}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "#64748b",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                        }}
                      >
                        Trainer Name
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "1.05rem", sm: "1.15rem" },
                          color: "#0f172a",
                          lineHeight: 1.3,
                        }}
                      >
                        {trainer.fullname}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Middle Column: Email and Phone Contact Info */}
                  <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {/* Email */}
                      {trainer.email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              backgroundColor: "#eff6ff",
                              border: "1px solid #dbeafe",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#2563eb",
                              flexShrink: 0,
                            }}
                          >
                            <EmailIcon sx={{ fontSize: "1rem" }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                color: "#64748b",
                                fontWeight: 500,
                                lineHeight: 1.2,
                              }}
                            >
                              Email
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.82rem",
                                color: "#1e293b",
                                fontWeight: 500,
                                wordBreak: "break-all",
                              }}
                            >
                              {trainer.email}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Phone */}
                      {trainer.phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              backgroundColor: "#ecfdf5",
                              border: "1px solid #a7f3d0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#059669",
                              flexShrink: 0,
                            }}
                          >
                            <PhoneIcon sx={{ fontSize: "1rem" }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                color: "#64748b",
                                fontWeight: 500,
                                lineHeight: 1.2,
                              }}
                            >
                              Phone
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.82rem",
                                color: "#1e293b",
                                fontWeight: 500,
                              }}
                            >
                              {trainer.phone}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Right Column: FDP & TechCamp Distinct Domains Chips */}
                  <Grid item xs={12} sm={12} md={4.5} lg={4.5}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                      {/* FDP Domains */}
                      {trainer.fdpDomains && trainer.fdpDomains.length > 0 && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              color: "#2563eb",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              mb: 0.5,
                            }}
                          >
                            FDP Domains
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {trainer.fdpDomains.map((dom, dIdx) => (
                              <Chip
                                key={dIdx}
                                label={dom}
                                size="small"
                                sx={{
                                  backgroundColor: "#eff6ff",
                                  color: "#1e40af",
                                  border: "1px solid #dbeafe",
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  height: "22px",
                                  borderRadius: "6px",
                                  "& .MuiChip-label": { px: 0.75, py: 0 },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* TechCamp Domains */}
                      {trainer.techcampDomains && trainer.techcampDomains.length > 0 && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              color: "#7c3aed",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              mb: 0.5,
                            }}
                          >
                            TechCamp Domains
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {trainer.techcampDomains.map((dom, dIdx) => (
                              <Chip
                                key={dIdx}
                                label={dom}
                                size="small"
                                sx={{
                                  backgroundColor: "#faf5ff",
                                  color: "#6b21a8",
                                  border: "1px solid #f3e8ff",
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  height: "22px",
                                  borderRadius: "6px",
                                  "& .MuiChip-label": { px: 0.75, py: 0 },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* ─────────────────────────────────────────────────────────────────
          MODAL: REQUEST TRAINER COMPONENT
      ───────────────────────────────────────────────────────────────── */}
      <RequestTrainerModal
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onTrainerRequested={handleTrainerRequested}
      />

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
